<template>
  <layout>
    <div
      class="pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.users.title") }}
        </h1>
        <primary-button
          v-if="hasPermission(permissions.createUser)"
          class="hidden sm:flex"
          :label="t('admin.settings.users.add_user')"
          @click="openSlideUserAdd = true"
        />
        <PlusIcon
          v-if="hasPermission(permissions.createUser)"
          class="w-10 text-gray-900 sm:hidden -mr-1"
          @click="openSlideUserAdd = true"
        />
      </div>
    </div>
    <div class="dynamic-height overflow-auto">
      <table
        class="min-w-full table-fixed border-0 border-separate border-spacing-0"
      >
        <thead class="sticky top-0 bg-gray-50 z-10">
          <tr>
            <th
              scope="col"
              class="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6 border-b border-gray-200"
            >
              {{ t("admin.settings.users.name.title") }}
            </th>
            <th
              scope="col"
              class="px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-b border-gray-200"
            >
              {{ t("admin.settings.users.roles.title") }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr v-for="invitation in props.invitations" :key="invitation.id">
            <td
              class="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-6 border-b border-gray-200"
            >
              <div class="flex items-center">
                <user-avatar :user="invitation" :size="6" />
                <div class="ml-4">
                  <div class="font-medium text-gray-900">
                    {{ invitation.email }}
                  </div>
                  <div
                    class="text-red-500 hover:text-red-600 font-medium cursor-pointer"
                    @click="handleCancelInvite(invitation)"
                  >
                    {{ t("admin.settings.users.cancel_invite") }}
                  </div>
                </div>
              </div>
            </td>
            <td
              class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium border-b border-gray-200"
            >
              <!-- @todo User roles need to be tenant based -->
              {{ getUserRoles(invitation) || "&mdash;" }}
            </td>
          </tr>
          <tr
            v-for="user in props.users"
            :key="user.id"
            class="hover:bg-gray-50 cursor-pointer"
            @click="handleUSerClicked(user)"
          >
            <td
              class="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-6 border-b border-gray-200"
            >
              <div class="flex items-center">
                <user-avatar :user="user" />
                <div class="ml-4">
                  <div class="font-medium text-gray-900">
                    {{ user.fullname }}
                  </div>
                  <div class="text-gray-500">{{ user.email }}</div>
                </div>
              </div>
            </td>
            <td
              class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium border-b border-gray-200"
            >
              <!-- @todo User roles need to be tenant based -->
              {{ getUserRoles(user) || "&mdash;" }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <settings-panel
      :open="openSlideUserAdd"
      :details="parsedUserDetails"
      :fields="userFields"
      :canCreate="hasPermission(permissions.createUser)"
      :canUpdate="hasPermission(permissions.updateUser)"
      :canDelete="hasPermission(permissions.deleteUser)"
      :createLabel="t('admin.settings.users.add_user')"
      :updateLabel="t('admin.settings.users.update_user')"
      :is-create-loading="isCreateLoading"
      :is-update-loading="isUpdateLoading"
      :is-delete-loading="isDeleteLoading"
      @close="handleUserPanelClose"
      @create="handleCreateUser"
      @update="handleUpdateUser"
      @delete="handleDeleteUser"
    />
  </layout>
</template>

<script setup>
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import Layout from "@/Tailwind/Layouts/TenantSettings";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton";
import { computed, defineProps, inject, onMounted, ref } from "vue";
import UserAvatar from "@/Tailwind/Components/UserAvatar";
import { capitalise } from "@/Tailwind/Helpers/text";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { usePage } from "@inertiajs/vue3";
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import SettingsPanel from "@/Tailwind/Components/Settings/SettingsPanel.vue";

import { useI18n } from "vue-i18n";
import { usePreloadStore } from "@/Tailwind/Stores/preload";
const { t } = useI18n();

defineOptions({ layout: PersistentLayout });

const props = defineProps({
  user: Object,
  users: Array,
  invitations: Array,
});

const preloadStore = usePreloadStore();

const tenant = usePage().props.tenant;
const openSlideUserAdd = ref(false);

const isCreateLoading = ref(false);
const isUpdateLoading = ref(false);
const isDeleteLoading = ref(false);

const { hasPermission, permissions } = inject("permissions");

const handleUSerClicked = (user) => {
  router.get(route("settings.users.edit", [tenant, user]));
};

const handleCancelInvite = (invitation) => {
  router.delete(route("settings.users.invite.cancel", [tenant, invitation]));
};

function handleBack() {
  router.visit(route("settings.tenant", [tenant]));
}

const getUserRoles = (user) => {
  if (user.roles.length === 0) {
    return;
  }

  return user.roles.map((role) => capitalise(role.name)).join(", ");
};

// User management
const userFields = computed(() => [
  {
    key: "avatar",
    title: t("admin.settings.users.photo.title"),
    description: t("admin.settings.users.photo.description"),
    type: "avatar",
    show: () => {
      return !!props.user;
    },
    extraAttributes: {
      details: parsedUserDetails.value,
    },
  },
  {
    key: "fullname",
    title: t("admin.settings.users.name.title"),
    description: t("admin.settings.users.name.description"),
    type: "composite",
    fields: [
      {
        key: "firstname",
        type: "text",
      },
      {
        key: "lastname",
        type: "text",
      },
    ],
    show: () => {
      return !!props.user;
    },
  },
  {
    key: "email",
    title: t("admin.settings.users.email.title"),
    description: t("admin.settings.users.email.description"),
    placeholder: t("admin.settings.users.email.placeholder"),
    type: "text",
  },
  {
    key: "role",
    title: t("admin.settings.users.roles.title"),
    description: t("admin.settings.users.roles.description"),
    placeholder: t("admin.settings.users.roles.placeholder"),
    type: "dropdown",
    options: roleOptions,
    extraAttributes: {
      selectorStyle: "input",
      multiple: false,
    },
  },
]);

const parsedUserDetails = computed(() => {
  if (!props.user) {
    return {};
  }

  return {
    ...props.user,
    role: props.user.roles[0]?.id,
  };
});

const roleOptions = usePage().props.roles?.map((role) => ({
  label: role.name,
  value: role.id,
}));

function handleCreateUser(inertiaForm) {
  inertiaForm
    .transform((data) => ({ ...data }))
    .post(route("settings.users.store", [tenant]), {
      onStart: () => (isCreateLoading.value = true),
      onSuccess: () => {
        preloadStore.loadUsers(true);
        handleUserPanelClose();
        isCreateLoading.value = false;
      },
      onError: () => (isCreateLoading.value = false),
    });
}

function handleUpdateUser(inertiaForm) {
  inertiaForm
    .transform((data) => ({
      ...data,
      // We need to act like a post request, but we want to use the put method
      // This is so we can send the image to the server
      _method: "put",
    }))
    .post(route("settings.users.update", [tenant, props.user]), {
      onStart: () => (isUpdateLoading.value = true),
      onSuccess: () => {
        preloadStore.loadUsers(true);
        handleUserPanelClose();
      },
      onError: () => (isUpdateLoading.value = false),
    });
}

function handleDeleteUser() {
  if (!hasPermission(permissions.deleteUser)) {
    return;
  }

  router.delete(route("settings.users.destroy", [tenant, props.user]), {
    onStart: () => (isDeleteLoading.value = true),
    onSuccess: () => {
      preloadStore.loadUsers(true);
      handleUserPanelClose();
    },
    onError: () => (isDeleteLoading.value = false),
  });
}

function handleUserPanelClose() {
  if (props.user) {
    router.get(route("settings.users.index", [tenant]));
  }

  openSlideUserAdd.value = false;
}

// -----------------------------

onMounted(() => {
  if (props.user) {
    openSlideUserAdd.value = true;
  }
});
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 87px);
}
</style>
